function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isStrictArrayIndex(segment: string): boolean {
  const index = Number(segment);
  return Number.isInteger(index) && String(index) === segment;
}

function normalizePath(displayedProperty: string): string[] {
  let normalized = displayedProperty;
  normalized = normalized.replace(/\[(\w+)]/g, '.$1');
  normalized = normalized.replace(/^\./, '');
  return normalized ? normalized.split('.') : [];
}

function resolvePathValue(source: unknown, pathSegments: string[]): unknown {
  let current = source;
  for (const segment of pathSegments) {
    if (Array.isArray(current)) {
      if (isStrictArrayIndex(segment)) {
        const index = Number(segment);
        if (index < 0 || index >= current.length) {
          return undefined;
        }
        current = current[index];
        continue;
      }

      if (current.length === 0) {
        return undefined;
      }
      current = current[0];
    }

    if (!isRecord(current) || !(segment in current)) {
      return undefined;
    }
    current = current[segment];
  }
  return current;
}

function formatAggregateValue(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value);
  }

  try {
    const jsonValue = JSON.stringify(value);
    if (jsonValue !== undefined) {
      return jsonValue;
    }
  } catch {
    // Fall back to String conversion below.
  }

  return String(value);
}

function aggregateResultValues(source: unknown, resultPath: string[]): string | undefined {
  if (!isRecord(source) || !('result' in source)) {
    return undefined;
  }

  const resultValue = source['result'];
  if (!Array.isArray(resultValue)) {
    const singleValue =
      resultPath.length > 0 ? resolvePathValue(resultValue, resultPath) : resultValue;
    return formatAggregateValue(singleValue);
  }

  const aggregatedValues: string[] = [];
  const seen = new Set<string>();

  for (const element of resultValue) {
    const value =
      resultPath.length > 0 ? resolvePathValue(element, resultPath) : element;
    const formatted = formatAggregateValue(value);
    if (!formatted || !formatted.trim() || seen.has(formatted)) {
      continue;
    }

    seen.add(formatted);
    aggregatedValues.push(formatted);
  }

  return aggregatedValues.length > 0 ? aggregatedValues.join('; ') : undefined;
}

function isResultAggregationPath(pathSegments: string[]): boolean {
  if (pathSegments.length === 0 || pathSegments[0] !== 'result') {
    return false;
  }

  if (pathSegments.length === 1) {
    return true;
  }

  return !isStrictArrayIndex(pathSegments[1]);
}

export function resolveColumnValue(
  source: unknown,
  displayedProperty: string,
): unknown {
  const pathSegments = normalizePath(displayedProperty);

  if (isResultAggregationPath(pathSegments)) {
    return aggregateResultValues(source, pathSegments.slice(1));
  }

  const resolvedValue = resolvePathValue(source, pathSegments);
  if (Array.isArray(resolvedValue)) {
    return resolvedValue.length > 0 ? resolvedValue : undefined;
  }
  return resolvedValue;
}

export function isResultColumn(displayedProperty: string): boolean {
  return displayedProperty === 'result' || displayedProperty.startsWith('result.');
}

export function truncateCellValue(
  value: string | undefined,
  limit: number,
): string | undefined {
  if (value === undefined || value.length <= limit) {
    return value;
  }

  return `${value.substring(0, limit)}...`;
}

export function isValueTruncated(
  value: string | undefined,
  limit: number,
): boolean {
  return value !== undefined && value.length > limit;
}
